import { useEffect, useRef } from 'react';

import { useAssetModalLeftPanel } from '~/components/AssetModalV2/AssetModalLeftPanelContextProvider';
import { useImageSearchContext } from '~/components/AssetModalV2/Content/Header/contexts/ImageSearchContextProvider';
import { clearPendingClipPersonIdRemap } from '~/hooks/people/clipPersonSelectionRemap';
import { clearAllSelectedPersonIds } from '~/hooks/people/useSelectedPersonId';
import { AssetModalPanel, useAssetModalPanelContext } from '~/providers/AssetModalPanelContextProvider';

export type UseAutoClearSelectedPersonOnTabCloseParams = {
  onPeopleUiClose?: () => void;
};

/** Clears people selection state when all people UI surfaces close. Mount once per modal session. */
export const useAutoClearSelectedPersonOnTabClose = ({
  onPeopleUiClose,
}: UseAutoClearSelectedPersonOnTabCloseParams = {}) => {
  const { panel } = useAssetModalPanelContext();
  const { isExpanded: isImageSearchExpanded } = useImageSearchContext();
  const { isLeftPanelTabOpen } = useAssetModalLeftPanel();

  const isPeopleTabOpen = panel === AssetModalPanel.PEOPLE;
  const isV2PeopleTabOpen = isLeftPanelTabOpen('people');
  const isPanelOpen = isPeopleTabOpen || isV2PeopleTabOpen;
  const isPeopleUiOpen = isPanelOpen || isImageSearchExpanded;
  const prevPeopleUiOpenRef = useRef(isPeopleUiOpen);
  const prevPanelOpenRef = useRef(isPanelOpen);
  const onPeopleUiCloseRef = useRef(onPeopleUiClose);
  onPeopleUiCloseRef.current = onPeopleUiClose;

  useEffect(() => {
    const wasPeopleUiOpen = prevPeopleUiOpenRef.current;
    const wasPanelOpen = prevPanelOpenRef.current;
    prevPeopleUiOpenRef.current = isPeopleUiOpen;
    prevPanelOpenRef.current = isPanelOpen;

    if (wasPanelOpen && !isPanelOpen) {
      clearAllSelectedPersonIds();
      clearPendingClipPersonIdRemap();
      onPeopleUiCloseRef.current?.();
    } else if (wasPeopleUiOpen && !isPeopleUiOpen) {
      onPeopleUiCloseRef.current?.();
    }
  }, [isPanelOpen, isPeopleUiOpen]);
};
